import { createSlice } from '@reduxjs/toolkit';
import {
  AccountActivationSubjectDto,
  HealthcheckDto,
  IpaProfile,
  IpaUser,
  JwtProfile,
  JwtTokenClaims,
  LoginAsDto,
  PasswordResetTokenProperties,
  RegistrationTokenRequestResponseDto,
  TempPasscodeResponseDto,
  UserDetailsDto,
  UserRegistrationResponse,
} from '@inosio/payments-sdk';
import jwtDecode from 'jwt-decode';
import { ErrorType, RootState } from '../../../app/store';
import { IPA_ACCESS_TOKEN_NAME, IPA_MFA_TOKEN_NAME } from '../../../utils/constants';

export class IpaUserExtended implements IpaUser {
  'userUuid': string; //uuid of the user at the keyboard, even if impersonating. Must be actual person. Maps to user.uuid
  'profiles': JwtProfile[];
  'currentProfile': IpaProfile;
  'username': string;
  accessToken!: string;
  mustChangePassword?: boolean;
}

type userRegErrorResponse = {
  statusCode: number;
  message: string;
  error: string;
};

type validateTokenErrorResponse = {
  statusCode: number;
  message: string;
  error: string;
};

type resetPasswordErrorResponse = {
  statusCode: number;
  message: string;
  error: string;
};

export type AuthState = {
  healthCheck: HealthcheckDto;
  healthCheckError: ErrorType;
  isFetchingHealthcheck: boolean;
  ipaUser: IpaUserExtended | null;
  mfaRequired: boolean;
  isFetching: boolean;
  isAddingProfile: boolean;
  isAddProfileSuccess: boolean;
  isAcceptedTerms: boolean;
  isAcceptingTerms: boolean;
  isSuccess: boolean;
  profileChange: boolean;
  isPasswordResetSuccess: boolean;
  resetPasswordErrorResponse: resetPasswordErrorResponse | null;
  isError: boolean;
  wrongPasswordAttempt: number,
  isLockedForSecurity: boolean,
  errorMessage: string | null;
  wrongPasswordAttemptErrorMessage: string| null
  accountActivationDetails: AccountActivationSubjectDto | null;
  userRegistrationDetails: UserRegistrationResponse | null;
  userRegistrationError: userRegErrorResponse | null;
  validateTokenErrorResponse: validateTokenErrorResponse | null;
  passwordResetTokenProperties: PasswordResetTokenProperties | null;
  userDetails: UserDetailsDto | null;
  isUpdatingUserDetails: boolean;
  isUpdateUserDetailsSuccess: boolean;
  updateUserDetailsError: any;
  resuiError: string;
  invitationDetails: RegistrationTokenRequestResponseDto;
  isSendingInvitationSuccess: boolean;
  isSendingInprogress: boolean;
  tempPasscodeDetails: TempPasscodeResponseDto;
  isIssuingTempPasscodeSuccess: boolean;
  isIssuingTempPasscodeFailed: boolean;
  issuingTempPasscodeError: string;
  sendingInvitationError: string;
  isSendingInvitationFailed: boolean;
  isLogAsSuccess: boolean;
  isLogAsFetching: boolean;
  isResettingPassword: boolean;
  //self registering owner
  isSelfInvitingProfile: boolean;
  isSelfInvitingProfileFailed: boolean;
  isSelfInvitingProfileSuccess: boolean;
  errorSelfInvitingProfile: ErrorType | null;
  requestPasswordResetError: ErrorType;
  isRequestingPasswordReset: boolean;
  isRequestPasswordResetSuccess: boolean;
};

const getDefaultState = () => {
  return {
    healthCheck: {} as HealthcheckDto,
    healthCheckError: {} as ErrorType,
    isFetchingHealthcheck: false,
    ipaUser: null,
    mfaRequired: false,
    isFetching: false,
    isAddingProfile: false,
    isAddProfileSuccess: false,
    isAcceptedTerms: false,
    isAcceptingTerms: false,
    isSuccess: false,
    profileChange: false,
    isPasswordResetSuccess: false,
    resetPasswordErrorResponse: null,
    isError: false,
    isLockedForSecurity: false,
    wrongPasswordAttempt : 0,
    errorMessage: null,
    wrongPasswordAttemptErrorMessage: null,
    accountActivationDetails: null,
    userRegistrationDetails: null,
    userRegistrationError: null,
    validateTokenErrorResponse: null,
    passwordResetTokenProperties: null,
    userDetails: null,
    isUpdatingUserDetails: false,
    isUpdateUserDetailsSuccess: false,
    updateUserDetailsError: null,
    resuiError: '',
    invitationDetails: {} as RegistrationTokenRequestResponseDto,
    isSendingInvitationSuccess: false,
    isSendingInprogress: true,
    tempPasscodeDetails: {} as TempPasscodeResponseDto,
    isIssuingTempPasscodeSuccess: false,
    isIssuingTempPasscodeFailed: false,
    issuingTempPasscodeError: '',
    sendingInvitationError: '',
    isSendingInvitationFailed: false,
    isLogAsSuccess: false,
    isLogAsFetching: false,
    isResettingPassword: false,
    isSelfInvitingProfile: false,
    isSelfInvitingProfileFailed: false,
    isSelfInvitingProfileSuccess: false,
    errorSelfInvitingProfile: null,
    isRequestingPasswordReset: false,
    isRequestPasswordResetSuccess: false,
    requestPasswordResetError: {} as ErrorType,
  } as AuthState;
};

const claimsToIpaUser = (accessToken: string) => {
  const jwtClaims = jwtDecode<JwtTokenClaims>(accessToken);

  let currentProfile: IpaProfile;
  //if acting, make that the current profile and that's that.
  if (jwtClaims.act?.uuid) {
    currentProfile = {
      isActing: true,
      role: jwtClaims.act.role,
      uuid: jwtClaims.act.uuid,
      impersonatingUserUuid: jwtClaims.act.impersonatingUserUuid,
    };
  } else {
    currentProfile = { ...jwtClaims.profiles.find((p) => p.uuid === jwtClaims.currentProfileUuid)!, isActing: false };
  }
  const userContext: IpaUserExtended = {
    userUuid: jwtClaims.sub,
    username: jwtClaims.username,
    profiles: jwtClaims.profiles,
    currentProfile: currentProfile,
    accessToken: accessToken,
  };

  return userContext;
};

const initialState = (): AuthState => {
  const existingAccessToken = sessionStorage.getItem(IPA_ACCESS_TOKEN_NAME);

  if (existingAccessToken) {
    try {
      const decodedJWT = jwtDecode<JwtTokenClaims>(existingAccessToken);
      if (decodedJWT.iat && decodedJWT.iat > 1742323375 && decodedJWT.exp && decodedJWT.exp > (new Date().getTime() | 0) / 1000) {
        return {
          ipaUser: claimsToIpaUser(existingAccessToken),
          isFetching: false,
          isSuccess: true,
          isError: false,
          errorMessage: null,
        } as AuthState;
      }

      sessionStorage.removeItem(IPA_ACCESS_TOKEN_NAME);
    } catch (error) {
      sessionStorage.removeItem(IPA_ACCESS_TOKEN_NAME);
    }
  }

  return getDefaultState();
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialState(),
  reducers: {
    getHealthCheck(state) {
      state.isFetchingHealthcheck = true;
      // state.healthCheck = {} as HealthcheckDto;
      state.healthCheckError = {} as ErrorType;
    },
    setHealthCheck(state, action) {
      state.isFetchingHealthcheck = false;
      state.healthCheck = action.payload;
      state.healthCheckError = {} as ErrorType;
    },
    setHealthCheckError(state, action) {
      state.healthCheck = {
        ApiVersion: '',
        UiVersion: '',
        Message: '',
        IsUiInMaintenanceMode: true, //safety net to true if the api call was an error
      };
      state.healthCheckError = action.payload;
      state.isFetchingHealthcheck = false;
    },
    getPasswordLogin(state, action) {
      state.isFetching = true;
      state.isSuccess = false;
      state.errorMessage = null;
      state.isError = false;
      state.mfaRequired = false;
    },
    setCredentials: (state, action) => {//invoke this when we received a full user token
      state.isFetching = false;
      state.errorMessage = null;
      state.isError = false;
      state.isSuccess = true;
      state.profileChange = action.payload.profileChange;
      state.mfaRequired = false;
      sessionStorage.removeItem(IPA_MFA_TOKEN_NAME);
      sessionStorage.setItem(IPA_ACCESS_TOKEN_NAME, action.payload.accessToken);
      const decodedJWT = { ...claimsToIpaUser(action.payload.accessToken), mustChangePassword: action.payload.mustChangePassword };
      state.ipaUser = decodedJWT;
    },
    loginFailed: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload.message;
      state.isLockedForSecurity = !!action.payload.isLockedForSecurity;
      state.wrongPasswordAttempt = action.payload.wrongPasswordAttempts;
      state.wrongPasswordAttemptErrorMessage = !!action.payload.wrongPasswordAttempts? `${action.payload.wrongPasswordAttempts} of ${action.payload.maxWrongPasswordAttempts} attempts used.`: null;
    },
    getMfaLogin(state){//invoke this to hit the mfa endpoint and get a full user token
      state.isFetching = true;
      state.isSuccess = false;
      state.errorMessage = null;
      state.isError = false;
    },
    setMfaRequired(state, action){//invoke this when we realize the user needs to do one more step to authenticate
      state.isFetching = false;
      state.errorMessage = null;
      state.isError = false;
      state.isSuccess = true;
      state.mfaRequired = true;
      sessionStorage.setItem(IPA_MFA_TOKEN_NAME, action.payload.accessToken);
    },
    clearCredentials: (state) => {
      sessionStorage.removeItem(IPA_ACCESS_TOKEN_NAME);
      sessionStorage.removeItem(IPA_MFA_TOKEN_NAME);
      state.ipaUser = null;
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
      state.wrongPasswordAttempt= 0
      state.isLockedForSecurity = false;
      state.wrongPasswordAttemptErrorMessage = null;
      state.mfaRequired = false;

    },
    switchProfileRequestReceived: (state, action) => {
      state.isFetching = true;
      state.errorMessage = null;
      state.isError = false;
    },
    switchProfileResponseReceived: (state, action) => {
      //todo, we may not need this.
      state.isFetching = false;
      state.errorMessage = null;
      state.isError = false;
      state.isSuccess = true;

      sessionStorage.setItem(IPA_ACCESS_TOKEN_NAME, action.payload.accessToken);
      const decodedJWT = claimsToIpaUser(action.payload.accessToken);
      state.ipaUser = decodedJWT;
    },
    getLoginAsEntity(state, action: { payload: LoginAsDto }) {
      state.isLogAsFetching = true;
      state.errorMessage = null;
      state.isError = false;
      state.isLogAsSuccess = false;
    },
    setLoginAsEntity(state, action) {
      state.errorMessage = null;
      state.isError = false;
      sessionStorage.setItem(IPA_ACCESS_TOKEN_NAME, action.payload.accessToken);
      const decodedJWT = claimsToIpaUser(action.payload.accessToken);
      state.ipaUser = decodedJWT;
      state.isLogAsFetching = false;
      state.isLogAsSuccess = true;

      //!why does this happen??
      // window.location.href = window.location.origin;
    },
    getAccountActivationDetails(state, action) {
      state.isFetching = true;
    },
    setAccountActivationDetails: (state, action) => {
      state.accountActivationDetails = action.payload;
      state.isFetching = false;
    },
    setValidateTokenError: (state, action) => {
      state.validateTokenErrorResponse = action.payload;
      state.isFetching = false;
    },
    clearValidateTokenError: (state, action) => {
      state.validateTokenErrorResponse = null;
      state.isFetching = false;
    },
    getRegisterUser(state, action) {
      state.isFetching = true;
      state.isSuccess = false;
    },
    setRegisterUser: (state, action) => {
      state.userRegistrationDetails = action.payload;
      state.isFetching = false;
      state.isError = false;
      state.userRegistrationError = null;
      const {
        payload: { status, accessToken },
      } = action;

      if (status === 'Success') {
        sessionStorage.setItem(IPA_ACCESS_TOKEN_NAME, accessToken);
        const decodedJWT = claimsToIpaUser(action.payload.accessToken);
        state.isSuccess = true;
        state.ipaUser = decodedJWT;
      }
    },
    getRegisterUserV2(state, action) {
      state.isFetching = true;
      state.isSuccess = false;
    },
    setRegisterUserV2: (state, action) => {
      state.userRegistrationDetails = action.payload;
      state.isFetching = false;
      state.isError = false;
      state.userRegistrationError = null;
      const {
        payload: { status, accessToken },
      } = action;

      if (status === 'Success') {
        sessionStorage.setItem(IPA_ACCESS_TOKEN_NAME, accessToken);
        const decodedJWT = claimsToIpaUser(action.payload.accessToken);
        state.isSuccess = true;
        state.ipaUser = decodedJWT;
      }
    },
    setRegisterUserError: (state, action) => {
      state.isSuccess = false;
      state.isError = true;
      state.userRegistrationError = action.payload;
      state.isFetching = false;
    },
    getPasswordResetRequest(state, action) {
      state.requestPasswordResetError = {} as ErrorType;
      state.isRequestingPasswordReset = true;
      state.isRequestPasswordResetSuccess = false;
    },
    setPasswordResetRequest(state, action) {
      state.requestPasswordResetError = {} as ErrorType;
      state.isRequestingPasswordReset = false;
      state.isRequestPasswordResetSuccess = true;
    },
    setPasswordResetRequestError(state, action) {
      state.requestPasswordResetError = action.payload;
      state.isRequestingPasswordReset = false;
      state.isRequestPasswordResetSuccess = false;
    },
    getPasswordRestTokenDetails(state, action) {
      state.isFetching = true;
      state.isSuccess = false;
      state.isError = false;
      state.validateTokenErrorResponse = null;
    },
    setPasswordRestTokenDetails(state, action) {//is this still needed?
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.passwordResetTokenProperties = action.payload;
    },
    getResetPasswordWithToken(state, action) {
      state.isPasswordResetSuccess = false;
      state.resetPasswordErrorResponse = null;
      state.isResettingPassword = true;
    },
    setResetPasswordWithToken(state, action) {
      state.isPasswordResetSuccess = true;
      state.resetPasswordErrorResponse = null;
      state.isResettingPassword = false;
    },
    setResetPasswordWithTokenError(state, action) {
      state.isPasswordResetSuccess = false;
      state.isResettingPassword = false;
      state.resetPasswordErrorResponse = action.payload;
    },
    getRefreshUser(state) {
      state.userRegistrationDetails = null;
      state.userRegistrationError = null;
      state.isError = false;
      state.errorMessage = null;
    },
    getAddProfile(state, action) {
      state.isAddingProfile = true;
      state.isError = false;
      state.errorMessage = null;
      state.isAddProfileSuccess = false;
    },
    setAddProfile(state, action) {
      state.isAddProfileSuccess = true;
      state.isAddingProfile = false;
      sessionStorage.setItem(IPA_ACCESS_TOKEN_NAME, action.payload.accessToken);

      const decodedJWT = claimsToIpaUser(action.payload.accessToken);

      state.ipaUser = decodedJWT;
    },
    setGlobalError(state, action) {
      state.errorMessage = action.payload.message;
    },
    getRecordAgreement(state, action) {
      state.isAcceptingTerms = true;
      state.isAcceptedTerms = false;
      state.isError = false;
      state.errorMessage = null;
    },
    setRecordAgreement(state) {
      state.isAcceptingTerms = false;
      state.isAcceptedTerms = true;
      state.errorMessage = null;
    },
    getUserDetails() {},
    setUserDetails(state, action) {
      state.userDetails = action.payload;
      state.updateUserDetailsError = null;
      state.isUpdatingUserDetails = false;
      state.isUpdateUserDetailsSuccess = true;
    },
    updateUserDetails(state, action) {
      state.updateUserDetailsError = null;
      state.isUpdatingUserDetails = true;
      state.isUpdateUserDetailsSuccess = false;
    },
    updateUserDetailsFailed(state, action) {
      state.updateUserDetailsError = action.payload;
      state.isUpdatingUserDetails = false;
      state.isUpdateUserDetailsSuccess = false;
    },
    clearUserDetailsResult(state, action) {
      state.updateUserDetailsError = null;
      state.isUpdatingUserDetails = false;
      state.isUpdateUserDetailsSuccess = false;
    },
    setResUiError(state, action) {
      state.resuiError = action.payload.message;
    },
    //#region
    sendInvitation(state, action) {
      state.invitationDetails = {} as RegistrationTokenRequestResponseDto;
      state.isSendingInvitationSuccess = false;
      state.isSendingInvitationFailed = false;
      state.sendingInvitationError = '';
      state.isSendingInprogress = true;
    },
    setSendInvitationResult(state, action) {
      const invitationResponse = { invitationDetails: action.payload, isSendingInprogress: false, isSendingInvitationSuccess: true, isSendingInvitationFailed: false };
      return { ...state, ...invitationResponse };
    },
    sendInvitationFailed(state, action) {
      state.invitationDetails = {} as RegistrationTokenRequestResponseDto;
      state.isSendingInvitationSuccess = false;
      state.sendingInvitationError = action.payload;
      state.isSendingInprogress = false;
      state.isSendingInvitationFailed = true;
    },
    clearInvitationResult(state, action) {
      state.invitationDetails = {} as RegistrationTokenRequestResponseDto;
      state.isSendingInprogress = false;
      state.sendingInvitationError = '';
      state.isSendingInvitationSuccess = false;
      state.isSendingInvitationFailed = false;
    },
    issueTempPasscode(state, action) {
      state.tempPasscodeDetails = {} as TempPasscodeResponseDto;
      state.issuingTempPasscodeError = '';
      state.isIssuingTempPasscodeSuccess = false;
      state.isIssuingTempPasscodeFailed = false;
    },
    setIssueTempPasscodeResult(state, action) {
      const tempPasscodeResponse = {
        tempPasscodeDetails: action.payload,
        isIssuingTempPasscodeSuccess: true,
        isIssuingTempPasscodeFailed: false,
      };
      return { ...state, ...tempPasscodeResponse };
    },
    issueTempPasscodeFailed(state, action) {
      state.tempPasscodeDetails = {} as TempPasscodeResponseDto;
      state.isIssuingTempPasscodeSuccess = false;
      state.isIssuingTempPasscodeFailed = true;
      state.issuingTempPasscodeError = action.payload;
    },
    clearTempPasscodeResult(state, action) {
      state.tempPasscodeDetails = {} as TempPasscodeResponseDto;
      state.issuingTempPasscodeError = '';
      state.isIssuingTempPasscodeSuccess = false;
      state.isIssuingTempPasscodeFailed = false;
    },
    selfInviteProfile(state) {
      state.isSelfInvitingProfile = true;
      state.isSelfInvitingProfileFailed = false;
      state.isSelfInvitingProfileSuccess = false;
      state.errorSelfInvitingProfile = null;
    },
    setSelfInviteProfile(state) {
      state.isSelfInvitingProfile = false;
      state.isSelfInvitingProfileFailed = false;
      state.isSelfInvitingProfileSuccess = true;
    },
    setErrorSelfInviteProfile(state, action) {
      state.isSelfInvitingProfile = false;
      state.isSelfInvitingProfileFailed = true;
      state.isSelfInvitingProfileSuccess = false;
      state.errorSelfInvitingProfile = action.payload;
    },
    resetSelfInviteProfileStates(state) {
      state.isSelfInvitingProfile = false;
      state.isSelfInvitingProfileFailed = false;
      state.isSelfInvitingProfileSuccess = false;
      state.errorSelfInvitingProfile = null;
    },
    //#endregion
  },
});

export const {
  getHealthCheck,
  setHealthCheck,
  setHealthCheckError,
  setResUiError,
  getUserDetails,
  setUserDetails,
  updateUserDetails,
  updateUserDetailsFailed,
  clearUserDetailsResult,
  getRecordAgreement,
  setGlobalError,
  setRecordAgreement,
  getAddProfile,
  setAddProfile,
  getRefreshUser,
  setPasswordResetRequest,
  getPasswordResetRequest,
  setPasswordResetRequestError,
  getPasswordLogin,
  setCredentials,
  getMfaLogin,
  setMfaRequired,
  switchProfileRequestReceived,
  loginFailed,
  clearCredentials,
  getAccountActivationDetails,
  setAccountActivationDetails,
  getRegisterUser,
  setRegisterUser,
  getRegisterUserV2,
  setRegisterUserV2,
  setRegisterUserError,
  setValidateTokenError,
  getPasswordRestTokenDetails,
  setPasswordRestTokenDetails,
  getResetPasswordWithToken,
  setResetPasswordWithToken,
  getLoginAsEntity,
  setLoginAsEntity,
  setResetPasswordWithTokenError,
  clearValidateTokenError,
  issueTempPasscode,
  setIssueTempPasscodeResult,
  issueTempPasscodeFailed,
  clearTempPasscodeResult,
  sendInvitation,
  setSendInvitationResult,
  sendInvitationFailed,
  clearInvitationResult,
  selfInviteProfile,
  setErrorSelfInviteProfile,
  setSelfInviteProfile,
  resetSelfInviteProfileStates,
} = authSlice.actions;

export default authSlice.reducer;
export const selectCurrentUser = (state: RootState) => state.auth.ipaUser;
export const selectAuthState = (state: RootState) => state.auth;
